import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';
import { HttpApiResponseInterface } from 'pf-frontend-common/src/module/http/api-response.interface';
import { createRef } from 'preact';

import { CountryCodeEnum } from 'common/data/country/code.enum';
import { PropertyReportClaimModel } from 'common/data/property-report/claim/model';
import { PropertyReportEligibilityModel } from 'common/data/property-report/eligibility/model';
import { PropertyReportModel } from 'common/data/property-report/model';
import { PropertyReportStore } from 'common/data/property-report/store';
import { UserUpdateModel } from 'common/data/user/update/model';
import { arrayLast } from 'common/helper/array/last';
import { configGetCountry } from 'common/helper/config/get-country';
import { i18nTranslate } from 'common/helper/i18n/translate';
import { ApiErrorInterface } from 'common/module/api/error.interface';
import { DataStore } from 'common/module/data/store';
import { GoogleRecaptchaViewStore } from 'common/module/google/recaptcha/view-store';
import { GoogleRecaptchaViewStoreEvent } from 'common/module/google/recaptcha/view-store.event';
import { InputValidatorInterface } from 'common/module/input/validator.interface';
import { InputValidatorFileSize } from 'common/module/input/validator/file-size';
import { InputValidatorFileType } from 'common/module/input/validator/file-type';
import { InputValidatorPropertyId } from 'common/module/input/validator/property-detail-url';
import { InputValidatorRequired } from 'common/module/input/validator/required';
import { ClaimTransactionStageType } from 'common/module/property-report/claim-transaction/stage.type';
import { PropertyReportDocumentType } from 'common/module/property-report/document.type';
import { propertyReportGroupedReasons } from 'common/module/property-report/grouped-reasons';
import { FormPropertyReportParamsInterface } from 'common/module/property-report/params.interface';
import { FormPropertyReportStatechart } from 'common/module/property-report/statechart';
import { FormPropertyReportStatechartEvent } from 'common/module/property-report/statechart.event';
import { FormPropertyReportStatechartActionEnum } from 'common/module/property-report/statechart/action.enum';
import { PropertyReportStatsTracker } from 'common/module/property-report/stats/tracker';
import { FormPropertyReportTemplatePropsInterface } from 'common/module/property-report/template-props.interface';
import { FormPropertyReportViewStoreEvent } from 'common/module/property-report/view-store.event';
import { FormPropertyReportViewStoreOptionsInterface } from 'common/module/property-report/view-store-options.interface';
import { StatechartStoreInterface } from 'common/module/statechart/store.interface';
import { UserAuthenticationServiceInterface } from 'common/service/user-authentication/service.interface';

import { InputValidatorEmail } from '../input/validator/email';
import { UserAuthFieldErrorsInterface } from '../user/auth/field-errors.interface';
import { UserAuthenticationEvent } from '../user/authentication.event';

export class FormPropertyReportViewStore extends DataStore<FormPropertyReportTemplatePropsInterface> {
  /**
   * User type agent
   */
  public static USER_TYPE_AGENT: string = 'agent';

  /**
   * Use type input field name
   */
  public static FIELD_NAME_REPORTER_TYPE: keyof FormPropertyReportParamsInterface = 'reporter_type';

  /**
   * Reason id input field name
   */
  public static FIELD_NAME_REASON_ID: keyof FormPropertyReportParamsInterface = 'reason_id';

  /**
   * Attachment input field name
   */
  public static FIELD_NAME_ATTACHMENT: keyof FormPropertyReportParamsInterface = 'attachment';

  /**
   * Attachment max file size in MB
   */
  public static MAX_FILE_UPLOAD_SIZE: number = 10;

  /**
   * Attachment file types
   */
  public static ACCEPTABLE_FILE_UPLOAD_TYPES: string[] = [
    'image/png',
    'image/jpg',
    'image/jpeg',
    'image/bmp',
    'application/pdf',
  ];
  /**
   * Claim my transaction reason_id
   */
  public static CMT_REASON_ID: number = 13;

  /**
   * Constructor
   */
  constructor(
    eventEmitter: EventEmitterInterface,
    private propertyReportStore: PropertyReportStore,
    protected captcha: GoogleRecaptchaViewStore,
    protected statechartStore: StatechartStoreInterface,
    private userAuthenticationService: UserAuthenticationServiceInterface,
    private claimTransactionTracker: PropertyReportStatsTracker
  ) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public getState(): FormPropertyReportTemplatePropsInterface {
    return this.state;
  }

  /**
   * @inheritDoc
   */
  public initialize(options: FormPropertyReportViewStoreOptionsInterface): void {
    if (options.resetObservers) {
      this.resetObservers();
    }

    const user = this.userAuthenticationService.getUser();
    this.setState(
      {
        status: {
          submitted: false,
          submitting: false,
        },
        emailRequired: !user?.email,
        captcha: this.captcha.getState(),
        error: '',
        model: options.model,
        report_id: '',
        showAttachment: false,
        showReporterType: this.isCountryAe(),
        params: {
          email: '',
          message: '',
          reason_id: 0,
          reporter_type: '',
          attachment: null,
          captcha_token: null,
        },
        inputs: {
          reasons: options.reasons.slice(),
          reporter_type: options.reporter_type.slice(),
        },
        allReasonList: options.reasons.slice(),
        fieldErrors: {
          email: '',
          message: '',
          reason_id: '',
          reporter_type: '',
          attachment: '',
          captcha_token: '',
        },
        fileInputRef: createRef<HTMLInputElement>(),
        onChange: this.onChange,
        onChangeReporterType: this.onChangeReporterType,
        onChangeFile: this.onChangeFile,
        onChangeCmtFile: this.onChangeCmtFile,
        onBlur: this.onBlur,
        onSubmit: this.onSubmit,
        onClickClose: this.onClickClose,
        reportSectionState: 'default',
        formSelectionsVisible: true,
        toggleFormSelectionsVisible: this.toggleFormSelectionsVisible,
        claimTransactionParams: {
          agentEmail: '',
          listingUrl: '',
          proofOfOwnership: '',
          proofOfTransaction: '',
          eligibilityResponseAttributes: null,
          additionalRefListing: '',
          claimTransactionResponseAttributes: {
            message: '',
            status: false,
            id: null,
          },
        },
        claimTransactionAttachments: {
          proofOfOwnership: null,
          proofOfTransaction: null,
        },
        onInputChange: this.onInputChange,
        onDocumentClear: this.clearCmtAttachment,
        checkEligible: this.checkEligible,
        onSubmitClaimTransaction: this.onSubmitClaimTransaction,
        stage: 'validate',
        cmtFieldErrors: {
          agentEmail: '',
          listingUrl: '',
          errorFromServer: '',
          proofOfOwnership: '',
          proofOfTransaction: '',
          additionalRefListing: '',
        },
        eligibilityCheckStatus: {
          checked: false,
          checking: false,
        },
        isMyListingSelected: false,
        agentInformation: options.model.agent,
        sendTealiumEvent: this.claimTransactionTracker,
        clearAttachment: this.clearAttachment,
      },
      this.initialize
    );

    this.statechartStore.initialize({
      statechart: FormPropertyReportStatechart,
    });
    this.observers();
  }

  /**
   * Attach observers
   */
  private observers(): void {
    this.captcha.getEventEmitter().addListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptchaViewStore);

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.validateFields, this.onValidateFieldsActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(
        FormPropertyReportStatechartActionEnum.checkIfHasAttachment,
        this.onCheckIfHasAttachmentActionStatechart
      );

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.uploadAttachment, this.onUploadAttachmentActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.sendCreateReportRequest, this.onSendCreateReportRequest);

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(
        FormPropertyReportStatechartActionEnum.checkIfWasAlreadySent,
        this.onCheckIfWasAlreadySentActionStatechart
      );

    this.statechartStore
      .getEventEmitter()
      .addListener(
        FormPropertyReportStatechartActionEnum.setSubmittedStatus,
        this.onSetSubmittedStatusActionStatechart
      );

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.showErrors, this.onShowErrorsActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.setSubmittingStatus, this.onSetSubmittingStatus);

    this.statechartStore
      .getEventEmitter()
      .addListener(FormPropertyReportStatechartActionEnum.doUserUpdate, this.handleUserUpdate);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.updateUserSucceeded, this.onUpdateUserSucceeded);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.updateUserFailed, this.onUpdateUserFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .addListener(UserAuthenticationEvent.updateUserValidationFailed, this.onUpdateUserFailed);
  }

  private resetObservers(): void {
    this.captcha.getEventEmitter().removeListener(GoogleRecaptchaViewStoreEvent.change, this.onChangeCaptchaViewStore);

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.validateFields, this.onValidateFieldsActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .removeListener(
        FormPropertyReportStatechartActionEnum.checkIfHasAttachment,
        this.onCheckIfHasAttachmentActionStatechart
      );

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.uploadAttachment, this.onUploadAttachmentActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.sendCreateReportRequest, this.onSendCreateReportRequest);

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.executeCaptcha, this.onExecuteCaptchaActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .removeListener(
        FormPropertyReportStatechartActionEnum.checkIfWasAlreadySent,
        this.onCheckIfWasAlreadySentActionStatechart
      );

    this.statechartStore
      .getEventEmitter()
      .removeListener(
        FormPropertyReportStatechartActionEnum.setSubmittedStatus,
        this.onSetSubmittedStatusActionStatechart
      );

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.showErrors, this.onShowErrorsActionStatechart);

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.setSubmittingStatus, this.onSetSubmittingStatus);

    this.statechartStore
      .getEventEmitter()
      .removeListener(FormPropertyReportStatechartActionEnum.doUserUpdate, this.handleUserUpdate);

    this.userAuthenticationService
      .getEventEmitter()
      .removeListener(UserAuthenticationEvent.updateUserSucceeded, this.onUpdateUserSucceeded);

    this.userAuthenticationService
      .getEventEmitter()
      .removeListener(UserAuthenticationEvent.updateUserFailed, this.onUpdateUserFailed);

    this.userAuthenticationService
      .getEventEmitter()
      .removeListener(UserAuthenticationEvent.updateUserValidationFailed, this.onUpdateUserFailed);
  }

  private handleUserUpdate = (): void => {
    if (!this.state.emailRequired) {
      this.statechartStore.transit({
        event: FormPropertyReportStatechartEvent.userUpdateSuccess,
        payload: null,
      });

      return;
    }
    const user = this.userAuthenticationService.getUser();
    const userUpdateModel = new UserUpdateModel();

    userUpdateModel.email = this.state.params.email;
    userUpdateModel.first_name = user?.first_name;

    this.userAuthenticationService.updateUser(userUpdateModel);
  };

  private onUpdateUserSucceeded = (): void => {
    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.userUpdateSuccess,
      payload: null,
    });
  };

  private onUpdateUserFailed = (errors: UserAuthFieldErrorsInterface | string): void => {
    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.userUpdateFailed,
      payload: errors,
    });
  };

  /**
   * Form validators
   */
  private getValidators(): {
    [key in keyof FormPropertyReportParamsInterface]: InputValidatorInterface[];
  } {
    const acceptableFileTypes = FormPropertyReportViewStore.ACCEPTABLE_FILE_UPLOAD_TYPES;

    return {
      reason_id: [new InputValidatorRequired(i18nTranslate('Please select a reason'))],
      reporter_type: this.isCountryAe() ? [new InputValidatorRequired(i18nTranslate('Please select user type'))] : [],
      attachment: [
        new InputValidatorFileType(
          i18nTranslate('Accepted formats are: {formats}').replace(
            '{formats}',
            acceptableFileTypes.map((item) => arrayLast(item.split('/'))).join(', ')
          ),
          acceptableFileTypes
        ),
        new InputValidatorFileSize(
          i18nTranslate('Uploaded file exceeds the maximum upload size'),
          FormPropertyReportViewStore.MAX_FILE_UPLOAD_SIZE
        ),
      ],
      message: [new InputValidatorRequired(i18nTranslate('Please add a comment'))],
      captcha_token: [],
      email: this.state.emailRequired
        ? [
            new InputValidatorRequired(i18nTranslate('Please enter your email')),
            new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
          ]
        : [],
    };
  }

  /**
   * Validate a single form field
   */
  private validateField(fieldName: keyof FormPropertyReportParamsInterface): boolean {
    this.state.fieldErrors[fieldName] =
      this.getValidators()
        [fieldName].map((validator) => validator.getError(this.state.params[fieldName], null))
        .filter((message) => !!message)[0] || '';

    this.setState(this.state);
    return this.state.fieldErrors[fieldName] === '';
  }

  /**
   * Update a param value
   */
  private setParam<K extends keyof FormPropertyReportParamsInterface>(
    param: K,
    value: FormPropertyReportParamsInterface[K]
  ): void {
    const state = this.getState();
    state.params[param] = value;
    this.setState(state, this.setParam);
  }

  private filterReasons = (user: string): void => {
    const selectedCategory = this.state.model?.category_identifier;
    if (this.isCountryAe() && selectedCategory) {
      this.setState({
        ...this.state,
        params: {
          ...this.state.params,
          [FormPropertyReportViewStore.FIELD_NAME_REASON_ID]: 0,
        },
        inputs: {
          ...this.state.inputs,
          reasons: this.state.allReasonList.filter((reason) => {
            return propertyReportGroupedReasons[selectedCategory][user].includes(Number(reason.value));
          }),
        },
      });
    } else {
      this.setState({
        ...this.state,
        params: {
          ...this.state.params,
          [FormPropertyReportViewStore.FIELD_NAME_REASON_ID]: 0,
        },
        inputs: {
          ...this.state.inputs,
          reasons: this.state.allReasonList,
        },
      });
    }
  };

  private toggleFormSelectionsVisible = (
    formSelectionsVisible: boolean,
    directedStage: ClaimTransactionStageType
  ): void => {
    this.setState({
      ...this.state,
      formSelectionsVisible,
      stage: directedStage,
    });
  };

  /**
   * Submit form
   */
  private submit(): void {
    const state = this.getState();
    const model = new PropertyReportModel();

    const user = this.userAuthenticationService.getUser();
    model.email = user?.email;
    model.user_phone = user?.phone;
    model.message = state.params.message;
    model.reason_id = state.params.reason_id;
    model.reporter_type = this.isCountryAe() ? state.params.reporter_type : '';
    this.propertyReportStore
      .createReport(state.model.id, model)
      .then(this.onSuccessCreatePropertyReport)
      .catch(this.onErrorCreatePropertyReport);
  }

  private sendMessageByCmtInformation(): string {
    const agentEmail = this.state.claimTransactionParams.agentEmail;
    const propertyId = this.getPropertyId(this.state.isMyListingSelected)?.toString();
    const competitorListing = this.state.claimTransactionParams.additionalRefListing;

    return `Agent Email: ${agentEmail}, Property Id: ${propertyId}, Competitor Listing: ${competitorListing}`;
  }

  private onSubmitClaimTransaction = (e: Event) => {
    e.preventDefault();
    const state = this.getState();
    this.setState({
      ...this.state,
      status: {
        ...this.state.status,
        submitting: true,
      },
    });

    const modelReport = new PropertyReportModel();
    const commentSectionForCmt = this.sendMessageByCmtInformation();
    const user = this.userAuthenticationService.getUser();

    modelReport.email = user?.email;
    modelReport.user_phone = user?.phone;
    modelReport.message = commentSectionForCmt;
    modelReport.reason_id = state.params.reason_id;
    modelReport.reporter_type = this.isCountryAe() ? state.params.reporter_type : '';
    if (this.state.report_id) {
      this.uploadCmtDocuments();
    } else {
      this.propertyReportStore
        .createReport(this.state.model.id, modelReport)
        .then(this.onSuccessCreatePropertyReport)
        .catch(this.onErrorCreatePropertyReport);
    }
  };

  /**
   * Upload attachment
   */
  private uploadAttachment(): void {
    this.propertyReportStore
      .uploadAttachment(this.state.report_id, this.state.params.attachment)
      .then(() =>
        this.statechartStore.transit({
          event: FormPropertyReportStatechartEvent.uploadSuccessful,
          payload: null,
        })
      )
      .catch((error) =>
        this.statechartStore.transit({
          event: FormPropertyReportStatechartEvent.uploadFailed,
          payload: error,
        })
      );
  }

  private uploadCmtDocuments(): void {
    const acceptableFileTypes = FormPropertyReportViewStore.ACCEPTABLE_FILE_UPLOAD_TYPES;
    const { proofOfOwnership, proofOfTransaction } = this.getState().claimTransactionAttachments;
    const validators = [
      new InputValidatorRequired(i18nTranslate('Please select a file')),
      new InputValidatorFileType(
        i18nTranslate('Accepted formats are: {formats}').replace(
          '{formats}',
          acceptableFileTypes.map((item) => arrayLast(item.split('/'))).join(', ')
        ),
        acceptableFileTypes
      ),
      new InputValidatorFileSize(
        i18nTranslate('Uploaded file exceeds the maximum upload size'),
        FormPropertyReportViewStore.MAX_FILE_UPLOAD_SIZE
      ),
    ];

    const proofOfTransactionError =
      validators.map((validator) => validator.getError(proofOfTransaction, null)).filter((message) => !!message)[0] ||
      '';
    const proofOfOwnershipError =
      validators.map((validator) => validator.getError(proofOfOwnership, null)).filter((message) => !!message)[0] || '';
    if (!proofOfTransactionError && !proofOfOwnershipError) {
      Promise.all([
        this.propertyReportStore.uploadAttachment(
          this.state.report_id,
          this.state.claimTransactionAttachments.proofOfTransaction
        ),
        this.propertyReportStore.uploadAttachment(
          this.state.report_id,
          this.state.claimTransactionAttachments.proofOfOwnership
        ),
      ])
        .then((responses) => {
          const [proofOfTransaction, proofOfOwnership] = responses;
          this.state.claimTransactionParams.proofOfTransaction = proofOfTransaction.data[0].attributes.url;
          this.claimTransactionTracker.onUploadClaimDocumentSuccess(
            'transaction',
            this.state.agentInformation,
            this.state.model.id
          );

          this.state.claimTransactionParams.proofOfOwnership = proofOfOwnership.data[0].attributes.url;
          this.claimTransactionTracker.onUploadClaimDocumentSuccess(
            'ownership',
            this.state.agentInformation,
            this.state.model.id
          );

          this.sendClaim();
        })
        .catch(() => {
          const { proofOfOwnership, proofOfTransaction } = this.state.claimTransactionParams;

          !proofOfOwnership &&
            this.claimTransactionTracker.onUploadClaimDocumentFail(
              'ownership',
              this.state.agentInformation,
              this.state.model.id
            );
          !proofOfTransaction &&
            this.claimTransactionTracker.onUploadClaimDocumentFail(
              'transaction',
              this.state.agentInformation,
              this.state.model.id
            );
          this.setState({
            ...this.state,
            cmtFieldErrors: {
              ...this.state.cmtFieldErrors,
              errorFromServer: i18nTranslate('Something went wrong! Please try again later'),
            },
            status: {
              submitting: false,
              submitted: false,
            },
          });
        });
    } else {
      this.setState({
        ...this.state,
        cmtFieldErrors: {
          ...this.state.cmtFieldErrors,
          proofOfOwnership: proofOfOwnershipError,
          proofOfTransaction: proofOfTransactionError,
        },
        status: {
          submitting: false,
          submitted: false,
        },
      });
    }
  }

  private sendClaim(): void {
    const { proofOfTransaction, proofOfOwnership, agentEmail, additionalRefListing } =
      this.getState().claimTransactionParams;

    const model = new PropertyReportClaimModel();
    model.property_id = this.getPropertyId(this.state.isMyListingSelected);
    model.doc_mou = proofOfTransaction;
    model.doc_pos = proofOfOwnership;
    model.agent_email = agentEmail;
    model.ref_listings = (!this.state.isMyListingSelected ? `${this.state.model.id},` : '') + additionalRefListing;
    this.propertyReportStore
      .submitClaim(model)
      .then((response) => {
        this.claimTransactionTracker.onCmtRequestSuccess(this.state.agentInformation, this.state.model.id);
        this.setState({
          ...this.state,
          status: {
            submitted: true,
            submitting: false,
          },
          cmtFieldErrors: {
            ...this.state.cmtFieldErrors,
            errorFromServer: response.data.attributes.message,
          },
        });
      })
      .catch(() => {
        this.claimTransactionTracker.onCmtRequestFail(this.state.agentInformation, this.state.model.id);
        this.setState({
          ...this.state,
          status: {
            ...this.state.status,
            submitting: false,
          },
          cmtFieldErrors: {
            ...this.state.cmtFieldErrors,
            errorFromServer: i18nTranslate('Something went wrong! Please try again later'),
          },
        });
      })
      .finally(() => {
        this.setState({
          ...this.state,
          status: {
            ...this.state.status,
            submitting: false,
          },
        });
      });
  }

  /**
   * Clear the file attachment field
   */
  private clearAttachment = (): void => {
    this.setParam(FormPropertyReportViewStore.FIELD_NAME_ATTACHMENT, null);
    this.validateField(FormPropertyReportViewStore.FIELD_NAME_ATTACHMENT);
  };
  private clearCmtAttachment = (documentType: PropertyReportDocumentType): void => {
    this.setState({
      ...this.state,
      claimTransactionAttachments: {
        ...this.state.claimTransactionAttachments,
        [documentType]: null,
      },
      cmtFieldErrors: {
        ...this.state.cmtFieldErrors,
        [documentType]: null,
      },
    });
  };

  /**
   * Check if country is ae
   */
  private isCountryAe(): boolean {
    return configGetCountry().code === CountryCodeEnum.ae;
  }

  /**
   * On property report created successfully
   */
  private onSuccessCreatePropertyReport = (data: PropertyReportModel) => {
    if (data && data.id) {
      this.state.report_id = data.id;
      this.setState(this.state);
    }

    if (this.state.params.reason_id === FormPropertyReportViewStore.CMT_REASON_ID) {
      this.uploadCmtDocuments();
    } else {
      this.claimTransactionTracker.onSendReport(
        {
          userType: this.state.params.reporter_type,
          reasonType: this.state.allReasonList.find((item) => item.value === this.state.params.reason_id)?.label,
        },
        this.state.agentInformation,
        this.state.model.id
      );

      this.statechartStore.transit({
        event: FormPropertyReportStatechartEvent.reportCreated,
        payload: null,
      });
    }
  };

  /**
   * When error while creating property report
   */
  private onErrorCreatePropertyReport = (response: Error | HttpApiResponseInterface) => {
    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.reportCreateFailed,
      payload: response,
    });
    this.setState({
      ...this.state,
      status: {
        submitting: false,
        submitted: false,
      },
    });
  };

  /**
   * Clicked close button
   */
  private onClickClose = (): void => {
    this.getEventEmitter().emit(FormPropertyReportViewStoreEvent.close);
  };

  /**
   * Submit form
   */
  private onSubmit = (e: Event): void => {
    e.preventDefault();
    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.wantToSendReport,
      payload: null,
    });
  };

  /**
   * Changed input
   */
  private onChange = (e: Event, fieldName: keyof FormPropertyReportParamsInterface) => {
    const value = (<HTMLInputElement>e.target).value;

    if (fieldName === FormPropertyReportViewStore.FIELD_NAME_REASON_ID) {
      const _value = parseInt(value, 10);
      const state = this.getState();
      this.setState({
        ...state,
        params: { ...state.params, [fieldName]: _value },
        reportSectionState: _value === FormPropertyReportViewStore.CMT_REASON_ID ? 'claim-validate' : 'default',
        emailRequired:
          _value === FormPropertyReportViewStore.CMT_REASON_ID
            ? false
            : !this.userAuthenticationService.getUser()?.email,
      });
      this.claimTransactionTracker.onSelectReason(
        this.state.allReasonList.find((item) => item.value === _value)?.label,
        this.state.agentInformation,
        this.state.model.id
      );
      return;
    }
    this.setParam(fieldName, value);
  };

  private onInputChange = (e: Event, fieldName: string) => {
    const value = (<HTMLInputElement>e.target).value;
    this.setState({
      ...this.state,
      claimTransactionParams: {
        ...this.state.claimTransactionParams,
        [fieldName]: value,
      },
      cmtFieldErrors: {
        ...this.state.cmtFieldErrors,
        [fieldName]: '',
      },
    });
  };

  /**
   * Changed reporter type
   */
  private onChangeReporterType = (e: Event) => {
    const value = (<HTMLInputElement>e.target).value;

    this.state.showAttachment = value === FormPropertyReportViewStore.USER_TYPE_AGENT && this.isCountryAe();
    if (value) {
      this.filterReasons(value);
    }

    this.setState({
      ...this.state,
      params: {
        ...this.state.params,
        [FormPropertyReportViewStore.FIELD_NAME_REASON_ID]: 0,
        [FormPropertyReportViewStore.FIELD_NAME_REPORTER_TYPE]: value,
      },
      reportSectionState: 'default',
    });
    this.clearAttachment();
    this.claimTransactionTracker.onSelectType(
      this.state.params.reporter_type,
      this.state.agentInformation,
      this.state.model.id
    );
  };

  /**
   * Get the property id with url or with state id. Regex for extracting id from url
   */
  private getPropertyId = (isAgentListing: boolean): number => {
    if (isAgentListing) {
      return Number(this.state.model.id);
    } else {
      const propertyIdMatch = /-(\d*)\.html/.exec(this.state.claimTransactionParams.listingUrl);
      return propertyIdMatch ? Number(propertyIdMatch[1]) : null;
    }
  };

  private validateCmtField(isAgentListing: boolean): boolean {
    const validators = {
      agentEmail: [
        new InputValidatorRequired(i18nTranslate('Please enter your email')),
        new InputValidatorEmail(i18nTranslate('Please enter a valid email')),
      ],
      listingUrl: !isAgentListing
        ? [
            new InputValidatorRequired(i18nTranslate('ReportProperty_EnterUrl')),
            new InputValidatorPropertyId(i18nTranslate('ReportProperty_ValidUrl')),
          ]
        : [],
    };

    const agentEmailError =
      validators.agentEmail
        .map((validator) => validator.getError(this.state.claimTransactionParams.agentEmail, null))
        .filter((message) => !!message)[0] || '';

    const listingUrlError =
      validators.listingUrl
        .map((validator) => validator.getError(this.state.claimTransactionParams.listingUrl, null))
        .filter((message) => !!message)[0] || '';

    this.setState({
      ...this.state,
      cmtFieldErrors: {
        ...this.state.cmtFieldErrors,
        agentEmail: agentEmailError,
        listingUrl: listingUrlError,
      },
    });

    return !agentEmailError && !listingUrlError;
  }

  private checkEligible = (isAgentListing: boolean) => {
    this.setState({
      ...this.state,
      isMyListingSelected: isAgentListing,
    });
    const model = new PropertyReportEligibilityModel();
    model.agent_email = this.state.claimTransactionParams.agentEmail;
    model.property_id = this.getPropertyId(isAgentListing);
    if (this.validateCmtField(isAgentListing)) {
      this.setState({
        ...this.state,
        eligibilityCheckStatus: {
          ...this.state.eligibilityCheckStatus,
          checking: true,
        },
      });
      this.propertyReportStore
        .checkEligible(model)
        .then(this.checkEligibleOnSuccess)
        .catch(this.checkEligibleOnFailure)
        .finally(() => {
          this.setState({
            ...this.state,
            eligibilityCheckStatus: {
              ...this.state.eligibilityCheckStatus,
              checking: false,
              checked: true,
            },
          });
        });
    }
  };
  private checkEligibleOnSuccess = (response: Pick<HttpApiResponseInterface, 'data'>) => {
    this.setState({
      ...this.state,
      claimTransactionParams: {
        ...this.state.claimTransactionParams,
        eligibilityResponseAttributes: response.data.attributes,
      },
      cmtFieldErrors: {
        ...this.state.cmtFieldErrors,
        errorFromServer: response.data.attributes.message,
      },
    });
    if (response.data.attributes.status) {
      this.claimTransactionTracker.onEligibilityValidateSuccess(
        this.state.isMyListingSelected,
        this.state.agentInformation,
        this.state.model.id
      );
      this.toggleFormSelectionsVisible(false, 'submit');
    } else {
      this.claimTransactionTracker.onEligibilityValidateFail(
        this.state.isMyListingSelected,
        this.state.agentInformation,
        this.state.model.id
      );
    }
  };
  private checkEligibleOnFailure = () => {
    this.claimTransactionTracker.onEligibilityValidateFail(
      this.state.isMyListingSelected,
      this.state.agentInformation,
      this.state.model.id
    );
    this.setState({
      ...this.state,
      stage: 'validate',
      cmtFieldErrors: {
        ...this.state.cmtFieldErrors,
        errorFromServer: i18nTranslate('Something went wrong! Please try again later'),
      },
    });
  };

  /**
   * File(s) selected
   */
  private onChangeFile = (e: any, fieldName: keyof FormPropertyReportParamsInterface) => {
    const files = e.target.files;
    this.setParam(FormPropertyReportViewStore.FIELD_NAME_ATTACHMENT, files[0]);
    this.validateField(fieldName);
  };
  /**
   * File(s) selected
   */
  private onChangeCmtFile = (e: any, documentType: PropertyReportDocumentType) => {
    const file = e.target.files;
    this.setState({
      ...this.state,
      claimTransactionAttachments: {
        ...this.state.claimTransactionAttachments,
        [documentType]: file[0],
      },
    });
  };

  /**
   * Blurred input
   */
  private onBlur = (e: Event, fieldName: keyof FormPropertyReportParamsInterface) => {
    this.validateField(fieldName);
  };

  /**
   * Captcha value has changed
   */
  private onChangeCaptchaViewStore = () => {
    const state = this.getState();

    state.params.captcha_token = this.captcha.getState().value;

    this.setState(state);

    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Validate fields
   */
  private onValidateFieldsActionStatechart = () => {
    let isValid = true;
    const validators = this.getValidators();

    Object.keys(validators).forEach((fieldName: keyof FormPropertyReportParamsInterface) => {
      isValid = isValid && this.validateField(fieldName);
    });

    this.setState(this.state);

    if (isValid) {
      this.statechartStore.transit({
        event: FormPropertyReportStatechartEvent.validationPassed,
        payload: null,
      });

      return;
    }

    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.validationFailed,
      payload: null,
    });
  };

  /**
   * Check if report was already sent (in case attachment was failed to upload)
   */
  private onCheckIfWasAlreadySentActionStatechart = () => {
    const state = this.getState();

    if (state.report_id && state.params.attachment) {
      this.statechartStore.transit({
        event: FormPropertyReportStatechartEvent.wasAlreadySent,
        payload: null,
      });

      return;
    }

    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.wasNotSent,
      payload: null,
    });
  };

  /**
   * Check if report has attachment
   */
  private onCheckIfHasAttachmentActionStatechart = () => {
    if (
      this.state.params.reporter_type === FormPropertyReportViewStore.USER_TYPE_AGENT &&
      this.state.params.attachment &&
      this.isCountryAe()
    ) {
      this.statechartStore.transit({
        event: FormPropertyReportStatechartEvent.hasAttachment,
        payload: null,
      });

      return;
    }

    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.hasNoAttachment,
      payload: null,
    });
  };

  /**
   * Upload attachment action handler
   */
  private onUploadAttachmentActionStatechart = () => {
    this.uploadAttachment();
  };

  /**
   * Send create report request
   */
  private onSendCreateReportRequest = () => {
    this.submit();
  };

  /**
   * Execute captcha
   */
  private onExecuteCaptchaActionStatechart = () => {
    if (!this.state.params.captcha_token) {
      this.captcha.execute();

      return;
    }

    this.statechartStore.transit({
      event: FormPropertyReportStatechartEvent.captchaValueChanged,
      payload: null,
    });
  };

  /**
   * Set submitted status (show success message)
   */
  private onSetSubmittedStatusActionStatechart = () => {
    this.state.status.submitting = false;
    this.state.status.submitted = true;
    this.getEventEmitter().emit(FormPropertyReportViewStoreEvent.submit);
    this.setState(this.state);
  };

  /**
   * Set submitting status (show loader)
   */
  private onSetSubmittingStatus = () => {
    this.state.status.submitting = true;
    this.state.error = '';

    this.setState(this.state);
  };

  /**
   * Show errors from server
   */
  private onShowErrorsActionStatechart = (response: Error | HttpApiResponseInterface | Record<string, string>) => {
    this.state.status.submitting = false;

    if (response instanceof Error) {
      throw new Error(response.message);
    }

    if (response.status === 400 || response.status === 422) {
      const errors = (<ApiErrorInterface[]>response.data.errors).reduce(
        (accumulator, error) => {
          const errorKey = error.source.pointer.split(/\.|\//).pop();
          return {
            ...accumulator,
            [errorKey]: error.detail,
          };
        },
        {
          email: '',
          reason_id: '',
          message: '',
          reporter_type: '',
          attachment: '',
          captcha_token: '',
        }
      );

      this.state.fieldErrors = errors;
    } else if ((response as Record<string, string>).email) {
      this.state.fieldErrors.email = (response as Record<string, string>).email;
    } else {
      this.state.error = i18nTranslate('Something went wrong! Please try again later');
    }

    this.setState(this.state);
  };
}
